/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * REST API を使用したユーザー管理 hooks
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type User = components["schemas"]["User"];

// =====================================
// useAllUsersRestApi - 全ユーザー一覧取得
// =====================================

export interface UseAllUsersRestApiOptions {
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseAllUsersRestApiResult {
  data: { allUsers: User[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<User[]>;
}

/**
 * 全ユーザー一覧を取得するhook
 * @param {UseAllUsersRestApiOptions} options - オプション設定
 * @return {UseAllUsersRestApiResult} ユーザー一覧、エラー、ローディング状態、refetch関数
 */
export function useAllUsersRestApi(
  options: UseAllUsersRestApiOptions = {},
): UseAllUsersRestApiResult {
  const [data, setData] = useState<{ allUsers: User[] } | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchAllUsers = useCallback(async (): Promise<User[]> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } =
        await api.GET("/api/users");

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ allUsers: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.onCompleted]);

  useEffect(() => {
    if (!options.skip) {
      fetchAllUsers().catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
    }
  }, [options.skip, fetchAllUsers]);

  return {
    data,
    error,
    loading,
    refetch: fetchAllUsers,
  };
}

export default {
  useAllUsersRestApi,
};
